import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, XCircle, AlertTriangle, CircleDot, CalendarDays, MapPin, Users, Target, FileText, Lightbulb, Bus, Flag, Clock, Timer, Coins, Phone, Car, User, Map, Pencil, ClipboardList, ArrowLeft } from 'lucide-react';

interface MatchdayPlan {
  id: number;
  name: string;
  description: string;
  localMatchdayId: number;
  departureLocation: string;
  destinationAddress: string;
  plannedDepartureTime: string;
  requiredArrivalTime: string;
  leadTimeMinutes: number;
  estimatedTravelDuration: number;
  totalParticipants: number;
  estimatedCost: number;
  status: number;
  emergencyContact: string;
  notes: string;
  transportations?: Transportation[];
}

interface Transportation {
  id: number;
  transportationName: string;
  driverName: string;
  assignedPassengers: number;
  estimatedCost: number;
  routeNotes: string;
  contactInfo: string;
}

interface LocalMatchday {
  id: number;
  name: string;
  location: string;
  description: string;
  date: string;
  expectedAttendees: number;
  organizer: string;
  notes: string;
}

const DemoHeimspiel: React.FC = () => {
  const navigate = useNavigate();
  const [matchdayPlan, setMatchdayPlan] = useState<MatchdayPlan | null>(null);
  const [localMatchday, setLocalMatchday] = useState<LocalMatchday | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDemoData();
  }, []);

  const fetchDemoData = async () => {
    try {
      setLoading(true);

      // Determine API base URL
      const configuredBase = (import.meta as any).env?.VITE_API_BASE as string | undefined;
      const apiBase = configuredBase && /^https?:\/\//i.test(configuredBase)
        ? configuredBase
        : window.location.origin;

      // Fetch LocalMatchday ID 5 (FC St. Pauli vs. Hoffenheim)
      const matchdayResponse = await fetch(`${apiBase}/api/LocalMatchday/5`);
      if (!matchdayResponse.ok) {
        throw new Error('Failed to fetch matchday data');
      }
      const matchdayData = await matchdayResponse.json();
      setLocalMatchday(matchdayData.data);

      // Fetch MatchdayPlan ID 2 (Anreise Heimspiel St. Pauli)
      const planResponse = await fetch(`${apiBase}/api/MatchdayPlan/2`);
      if (!planResponse.ok) {
        throw new Error('Failed to fetch matchday plan');
      }
      const planData = await planResponse.json();
      setMatchdayPlan(planData.data);

      setError(null);
    } catch (err) {
      console.error('Error fetching demo data:', err);
      setError('Fehler beim Laden der Demo-Daten. Bitte stellen Sie sicher, dass die Demo-Daten erstellt wurden.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={loadingStyle}>Lade Demo-Daten...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={errorStyle}>
          <h2><XCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Fehler</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/matchday-plans')} style={buttonStyle}>
            Zurück zu Spieltagsplänen
          </button>
        </div>
      </div>
    );
  }

  if (!matchdayPlan || !localMatchday) {
    return (
      <div className="page-container" style={containerStyle}>
        <div style={errorStyle}>
          <h2><AlertTriangle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Keine Daten gefunden</h2>
          <p>Die Demo-Daten wurden noch nicht erstellt.</p>
          <button onClick={() => navigate('/matchday-plans')} style={buttonStyle}>
            Zurück zu Spieltagsplänen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={containerStyle}>
      <div style={headerContainerStyle}>
        <button onClick={() => navigate('/matchday-plans')} style={backButtonStyle}>
          <ArrowLeft size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Zurück
        </button>
        <h1 style={titleStyle}><Home size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Demo: Heimspiel</h1>
      </div>

      {/* Matchday Info Card */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}><CircleDot size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {localMatchday.name}</h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <strong><CalendarDays size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Datum:</strong> {formatDateTime(localMatchday.date)}
          </div>
          <div style={infoItemStyle}>
            <strong><MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ort:</strong> {localMatchday.location}
          </div>
          <div style={infoItemStyle}>
            <strong><Users size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Erwartete Zuschauer:</strong> {localMatchday.expectedAttendees?.toLocaleString('de-DE')}
          </div>
          <div style={infoItemStyle}>
            <strong><Target size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Veranstalter:</strong> {localMatchday.organizer}
          </div>
        </div>
        {localMatchday.description && (
          <div style={descriptionStyle}>
            <strong><FileText size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Beschreibung:</strong> {localMatchday.description}
          </div>
        )}
        {localMatchday.notes && (
          <div style={notesStyle}>
            <strong><Lightbulb size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Hinweise:</strong> {localMatchday.notes}
          </div>
        )}
      </div>

      {/* Matchday Plan Card */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}><Bus size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {matchdayPlan.name}</h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <strong><Flag size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Abfahrtsort:</strong> {matchdayPlan.departureLocation}
          </div>
          <div style={infoItemStyle}>
            <strong><Target size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Zielort:</strong> {matchdayPlan.destinationAddress}
          </div>
          <div style={infoItemStyle}>
            <strong><Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Abfahrt:</strong> {formatTime(matchdayPlan.plannedDepartureTime)}
          </div>
          <div style={infoItemStyle}>
            <strong><Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ankunft:</strong> {formatTime(matchdayPlan.requiredArrivalTime)}
          </div>
          <div style={infoItemStyle}>
            <strong><Timer size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Reisedauer:</strong> {matchdayPlan.estimatedTravelDuration} Minuten
          </div>
          <div style={infoItemStyle}>
            <strong><Users size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Teilnehmer:</strong> {matchdayPlan.totalParticipants}
          </div>
          <div style={infoItemStyle}>
            <strong><Coins size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Geschätzte Kosten:</strong> {matchdayPlan.estimatedCost.toFixed(2)} €
          </div>
          <div style={infoItemStyle}>
            <strong><Phone size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Notfallkontakt:</strong> {matchdayPlan.emergencyContact}
          </div>
        </div>
        {matchdayPlan.description && (
          <div style={descriptionStyle}>
            <strong><FileText size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Beschreibung:</strong> {matchdayPlan.description}
          </div>
        )}
        {matchdayPlan.notes && (
          <div style={notesStyle}>
            <strong><Lightbulb size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Hinweise:</strong> {matchdayPlan.notes}
          </div>
        )}
      </div>

      {/* Transportation Card */}
      {matchdayPlan.transportations && matchdayPlan.transportations.length > 0 && (
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}><Car size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Verkehrsmittel</h2>
          {matchdayPlan.transportations.map((transport, index) => (
            <div key={index} style={transportCardStyle}>
              <h3 style={transportTitleStyle}>{transport.transportationName}</h3>
              <div style={infoGridStyle}>
                <div style={infoItemStyle}>
                  <strong><User size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Fahrer:</strong> {transport.driverName}
                </div>
                <div style={infoItemStyle}>
                  <strong><Users size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Passagiere:</strong> {transport.assignedPassengers}
                </div>
                <div style={infoItemStyle}>
                  <strong><Coins size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Kosten:</strong> {transport.estimatedCost.toFixed(2)} €
                </div>
                <div style={infoItemStyle}>
                  <strong><Phone size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Kontakt:</strong> {transport.contactInfo}
                </div>
              </div>
              {transport.routeNotes && (
                <div style={routeNotesStyle}>
                  <strong><Map size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Routenhinweise:</strong> {transport.routeNotes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div style={actionButtonsStyle}>
        <button onClick={() => navigate(`/matchday-plans/${matchdayPlan.id}`)} style={primaryButtonStyle}>
          <Pencil size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Plan bearbeiten
        </button>
        <button onClick={() => navigate('/matchday-plans')} style={secondaryButtonStyle}>
          <ClipboardList size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Alle Spieltagspläne
        </button>
      </div>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '2rem',
};

const headerContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '2rem',
};

const backButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  color: '#2c3e50',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '2rem',
  marginBottom: '2rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const cardTitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: '1.5rem',
  color: '#2c3e50',
  borderBottom: '2px solid #3498db',
  paddingBottom: '0.5rem',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1rem',
  marginBottom: '1rem',
};

const infoItemStyle: React.CSSProperties = {
  padding: '0.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '4px',
};

const descriptionStyle: React.CSSProperties = {
  padding: '1rem',
  backgroundColor: '#e3f2fd',
  borderRadius: '4px',
  marginTop: '1rem',
};

const notesStyle: React.CSSProperties = {
  padding: '1rem',
  backgroundColor: '#fff3cd',
  borderRadius: '4px',
  marginTop: '1rem',
};

const transportCardStyle: React.CSSProperties = {
  padding: '1rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '4px',
  marginBottom: '1rem',
};

const transportTitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: '1rem',
  color: '#495057',
};

const routeNotesStyle: React.CSSProperties = {
  padding: '0.75rem',
  backgroundColor: '#d1ecf1',
  borderRadius: '4px',
  marginTop: '1rem',
};

const actionButtonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  fontSize: '1.2rem',
  color: '#6c757d',
};

const errorStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  backgroundColor: '#f8d7da',
  borderRadius: '8px',
  color: '#721c24',
};

const buttonStyle: React.CSSProperties = {
  marginTop: '1rem',
  padding: '0.75rem 1.5rem',
  backgroundColor: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

export default DemoHeimspiel;

